import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, User, Eye, CheckCircle, Save, XCircle, Clock, ChevronLeft, ChevronDown, ChevronRight, DollarSign, UserPlus, Info, AlertTriangle, Users, MessageSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout';
import { PageHeader, SectionCard, EmptyState, StatusBadge, BackLink, EditableFormSection, ReadOnlyValue, RadioGroup } from '@/components/shared/DashboardUI';
import ProfileTab from '@/components/shared/ProfileTab';
import '@/styles/authenticated.css';
import { formatPhoneNumber, parsePhoneNumber, formatZipCode, formatCurrency, parseCurrency, US_STATES, CASE_TYPES, INSURANCE_CARRIERS, LIABILITY_LIMITS, formatDate, formatDateOnly, getCaseTypeLabel, getInsuranceCarrierLabel, getLiabilityLimitsLabel, TREATMENT_LOCATIONS, TREATMENTS_COMPLETED_OPTIONS, formatTreatmentLocations } from '@/lib/formatters';
import CompareOffers from '@/components/CompareOffers';
import { DocumentUpload } from '@/components/DocumentUpload';
import DocumentRequestsCard from '@/components/DocumentRequestsCard';
import MessagesPanel from '@/components/shared/MessagesPanel';
import MessagesInbox from '@/components/shared/MessagesInbox';
import { ActivityTimelineCard } from '@/components/ActivityTimeline';
import TeamMemberSelect from '@/components/TeamMemberSelect';
import TeamMembersTab from '@/components/TeamMembersTab';
import { validateApplicationFormats } from '@/lib/applicationValidation';


const NAV_ITEMS_LAWYER = [
  { id: 'applications', label: 'Client Applications', icon: FileText },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'refer-client', label: 'Refer a Client', icon: UserPlus },
  { id: 'client-offers', label: 'Client Offers', icon: DollarSign },
  { id: 'team-members', label: 'Team Members', icon: Users },
  { id: 'profile', label: 'Profile', icon: User },
];

// Paralegals see the same dashboard EXCEPT the Team Members management nav.
const NAV_ITEMS_PARALEGAL = [
  { id: 'applications', label: 'Client Applications', icon: FileText },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'refer-client', label: 'Refer a Client', icon: UserPlus },
  { id: 'client-offers', label: 'Client Offers', icon: DollarSign },
  { id: 'profile', label: 'Profile', icon: User },
];

const LawyerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[2] === 'application' && pathParts[3]) { setSelectedApplicationId(pathParts[3]); setActiveTab('applications'); }
    else if (pathParts[2] && ['applications', 'messages', 'refer-client', 'client-offers', 'team-members', 'profile'].includes(pathParts[2])) {
      setActiveTab(pathParts[2]); setSelectedApplicationId(null);
    }
    else { setSelectedApplicationId(null); }
  }, [location.pathname]);

  useEffect(() => { fetchUser(); }, []);


  const fetchUser = async () => {
    try { setUser(await apiGet('/users/me')); } catch (error) { console.error('Failed to fetch user:', error); }
  };

  const handleNavClick = (tab) => { setActiveTab(tab); setSelectedApplicationId(null); navigate(tab === 'applications' ? '/lawyer' : `/lawyer/${tab}`); };
  const handleViewApplication = (appId) => { navigate(`/lawyer/application/${appId}`); };
  const handleBackToList = () => { setSelectedApplicationId(null); navigate('/lawyer'); };

  const pageTitles = { applications: 'Client Applications', messages: 'Messages', 'refer-client': 'Refer a Client', 'client-offers': 'Client Offers', 'team-members': 'Team Members', profile: 'Profile' };
  const isParalegal = user?.role === 'paralegal';
  const navItems = isParalegal ? NAV_ITEMS_PARALEGAL : NAV_ITEMS_LAWYER;

  // Gate: Lawyer must be approved to access dashboard
  if (user && user.lawyer_approved === false) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '2rem 1rem' }}>
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }} data-testid="lawyer-pending-approval">
          <div style={{ background: '#fff', borderRadius: 12, padding: '3rem 2.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <Clock size={36} style={{ color: '#d97706' }} />
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>Account Pending Approval</h1>
            <p style={{ color: '#64748b', lineHeight: 1.7, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Thank you for registering with MyCaseLoan.com! Your attorney account is currently under review. You will receive an email once your account has been approved by our team.
            </p>
            <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                <strong>What happens next?</strong><br />
                Our admin team will review your registration and approve your account. Once approved, you&apos;ll have full access to manage client applications, review offers, and refer clients for funding.
              </p>
            </div>
            <button className="mcl-btn mcl-btn-outline" onClick={() => { localStorage.removeItem('access_token'); navigate('/login'); }} data-testid="lawyer-pending-logout-btn">
              Back to Login
            </button>
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '1.5rem' }}>&copy; 2026 MyCaseLoan.com. All rights reserved.</p>
        </div>
      </div>
    );
  }

  return (
    <AuthenticatedLayout user={user} activeTab={activeTab} onTabChange={handleNavClick} navItems={navItems} role={isParalegal ? 'paralegal' : 'lawyer'} pageTitle={pageTitles[activeTab]}>
      {activeTab === 'applications' && <ApplicationsTab selectedApplicationId={selectedApplicationId} onViewApplication={handleViewApplication} onBackToList={handleBackToList} onReferClient={() => handleNavClick('refer-client')} currentUser={user} />}
      {activeTab === 'messages' && <MessagesInbox currentUser={user} />}
      {activeTab === 'refer-client' && <ReferClientTab user={user} onSuccess={() => handleNavClick('applications')} />}
      {activeTab === 'client-offers' && <ClientOffersTab onViewApplication={handleViewApplication} />}
      {activeTab === 'team-members' && !isParalegal && <TeamMembersTab />}
      {activeTab === 'profile' && <ProfileTab user={user} onUpdate={fetchUser} />}
    </AuthenticatedLayout>
  );
};

/* ================================================================
   Applications Tab
   ================================================================ */
const ApplicationsTab = ({ selectedApplicationId, onViewApplication, onBackToList, onReferClient, currentUser }) => {
  const [applications, setApplications] = useState([]);
  const [viewingApplication, setViewingApplication] = useState(null);


  const fetchApplications = async () => {
    try { setApplications(await apiGet('/applications')); } catch (error) { console.error('Failed to fetch applications:', error); }
  };

  useEffect(() => { fetchApplications(); }, []);

  useEffect(() => {
    if (selectedApplicationId && applications.length > 0) { const app = applications.find(a => a.application_id === selectedApplicationId); if (app) setViewingApplication(app); }
    else if (!selectedApplicationId) setViewingApplication(null);
  }, [selectedApplicationId, applications]);

  const handleUpdateStatus = async (appId, newStatus) => {
    try {
      await apiPut(`/applications/${appId}`, { status: newStatus });
      toast.success('Status updated'); fetchApplications();
    } catch (error) { toast.error(error.message || 'Failed to update status'); }
  };

  if (viewingApplication) {
    const siblingIds = applications.map(a => a.application_id);
    return (
      <LawyerApplicationDetailView
        application={viewingApplication}
        siblingIds={siblingIds}
        currentUser={currentUser}
        onNavigate={(appId) => onViewApplication ? onViewApplication(appId) : setViewingApplication(applications.find(a => a.application_id === appId))}
        onBack={() => onBackToList ? onBackToList() : setViewingApplication(null)}
        onUpdate={() => { fetchApplications(); if (onBackToList) onBackToList(); else setViewingApplication(null); }}
        onStatusChange={handleUpdateStatus}
      />
    );
  }

  return (
    <div data-testid="applications-tab">
      <PageHeader title="Client Applications" subtitle="Review and manage your clients' funding applications" actions={
        <button className="mcl-btn mcl-btn-primary" onClick={onReferClient} data-testid="refer-client-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <UserPlus size={16} /> Refer a Client
        </button>
      } />
      <SectionCard title={`All Applications (${applications.length})`}>
        {applications.length === 0 ? (
          <EmptyState icon={FileText} title="No applications yet" text="Client applications assigned to you will appear here." />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table">
              <thead><tr><th>Applicant</th><th>Case Type</th><th>Amount</th><th>Status</th><th>Offers</th><th>Validated</th><th>Action</th></tr></thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.application_id} data-testid={`application-${app.application_id}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{app.firstName} {app.lastName}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>ID: {app.application_id?.slice(0, 8)}...</div>
                    </td>
                    <td>{getCaseTypeLabel(app.caseType || app.case_type)}</td>
                    <td style={{ fontWeight: 600 }}>{app.requestedAmount ? `$${parseFloat(app.requestedAmount).toLocaleString()}` : '-'}</td>
                    <td><StatusBadge status={app.status} /></td>
                    <td>
                      {app.total_offers_count > 0 ? (
                        <span className={`mcl-badge ${app.accepted_offers_count > 0 ? 'mcl-badge-success' : 'mcl-badge-primary'}`} style={{ fontSize: '0.7rem' }}>
                          {app.accepted_offers_count > 0 ? `${app.accepted_offers_count} Accepted` : `${app.pending_offers_count} Pending`}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--mcl-text-light)', fontSize: '0.8rem' }}>None</span>
                      )}
                    </td>
                    <td>{app.validated_at ? <span className="mcl-badge mcl-badge-success">Validated</span> : <span style={{ color: 'var(--mcl-text-light)', fontSize: '0.8rem' }}>Not yet</span>}</td>
                    <td>
                      <button className="mcl-action-btn primary" onClick={() => onViewApplication ? onViewApplication(app.application_id) : setViewingApplication(app)} data-testid={`view-button-${app.application_id}`}>
                        <Eye size={14} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

/* ================================================================
   Lawyer Application Detail View
   ================================================================ */
/* ============================================================
   Phase 1 UX — sticky action panel, snapshot, readiness gauge,
   missing-info alerts, collapsible sections. Layout-only helpers.
   ============================================================ */

// No documents are strictly required before approval — all supporting docs
// are recommended (they materially improve funder offers but don't block approval).
const DOC_REQUIRED = [];
const DOC_RECOMMENDED = [
  'attorney_representation_letter',
  'police_accident_report',
  'insurance_declaration_page',
  'medical_records',
  'demand_letter',
];

const DOC_LABELS = {
  attorney_representation_letter: 'Attorney Representation Letter',
  police_accident_report: 'Police / Accident Report',
  insurance_declaration_page: 'Insurance Declaration Page',
  medical_records: 'Medical Records',
  demand_letter: 'Demand Letter',
};

// Required fields a lawyer must confirm before they can approve. Keys match
// formData keys; labels are what we show in the toast/disabled-button hint.
const APPROVAL_REQUIRED_FIELDS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'caseType', label: 'Case Type' },
  { key: 'requestedAmount', label: 'Requested Amount' },
  { key: 'accidentState', label: 'Accident State' },
  { key: 'insuranceCarrier', label: 'Insurance Carrier' },
  { key: 'hasUmCoverage', label: 'UM Coverage (Yes/No)' },
  { key: 'attorneyEmail', label: 'Attorney Email' },
  { key: 'lawFirmName', label: 'Law Firm' },
  { key: 'lawFirmPhone', label: 'Law Firm Phone' },
];

const getMissingApprovalFields = (d) => {
  if (!d) return APPROVAL_REQUIRED_FIELDS.map(f => f.label);
  return APPROVAL_REQUIRED_FIELDS.filter((f) => {
    const v = d[f.key];
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
  }).map(f => f.label);
};

const computeFundingReadiness = (app, docs) => {
  const checks = [];
  checks.push({
    key: 'attorney_identified',
    label: 'Attorney Identified',
    passed: !!(app.attorneyEmail && app.lawFirmName),
  });
  checks.push({
    key: 'treatment',
    label: 'Treatment Information',
    passed: !!(app.hasStartedTreatment || app.injuriesDescription || app.hadPtOrChiro),
  });
  checks.push({
    key: 'insurance',
    label: 'Insurance Identified',
    passed: !!app.insuranceCarrier,
  });
  checks.push({
    key: 'policy_limits',
    label: 'Policy Limits Specified',
    passed: !!app.liabilityLimits,
  });
  const uploadedSlugs = new Set((docs || []).map(d => d.document_category));
  const recommendedUploaded = DOC_RECOMMENDED.filter(s => uploadedSlugs.has(s)).length;
  // Documents are NOT a readiness gate — they're recommendations surfaced
  // separately in the "Recommended (improves funder offers)" alert. In the
  // future, funders will be able to request specific docs from the lawyer.
  const passed = checks.filter(c => c.passed).length;
  const pct = Math.round((passed / checks.length) * 100);
  return { pct, checks, missingRequired: [], recommendedUploaded };
};

const computeMissingDocs = (docs) => {
  const uploaded = new Set((docs || []).map(d => d.document_category));
  return {
    required: DOC_REQUIRED.filter(s => !uploaded.has(s)),
    recommended: DOC_RECOMMENDED.filter(s => !uploaded.has(s)),
    uploadedSet: uploaded,
  };
};

const CollapsibleSection = ({ title, defaultOpen = false, badge, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mcl-card" style={{ marginBottom: '1rem' }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="mcl-card-header"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '0.85rem 1.1rem', textAlign: 'left',
        }}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{title}</span>
          {badge}
        </span>
      </button>
      {open && <div style={{ padding: '0 1.1rem 1.1rem' }}>{children}</div>}
    </div>
  );
};

/* Activity Timeline moved to /app/frontend/src/components/ActivityTimeline.js so
   client + funder views can render an identical card. */

// Named export so the panel's opening position can be asserted against
// rendered output rather than inferred.
export const LawyerApplicationDetailView = ({ application, onBack, onUpdate, onStatusChange, siblingIds = [], onNavigate, currentUser }) => {
  const [formData, setFormData] = useState({ ...application });
  const [originalData, setOriginalData] = useState({ ...application });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Disabling the button is the visible half; this is the half that actually
  // holds. A double-click can land two calls before React re-renders with the
  // disabled state, and each would PUT the section independently.
  const savingRef = useRef(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineFeedback, setDeclineFeedback] = useState('');
  const [validating, setValidating] = useState(false);
  const [declining, setDeclining] = useState(false);
  // Documents list — feeds the readiness gauge + missing-info alerts. We refetch
  // after each upload via the onUploaded callback wired into DocumentUpload.
  const [docs, setDocs] = useState([]);
  // Funding Activity Timeline — pulls offers so we can render submitted/accepted events.
  const [offers, setOffers] = useState([]);
  // Document requests — funder-created requests + their fulfillment states, added to the timeline.
  const [documentRequests, setDocumentRequests] = useState([]);
  // Team members — used to resolve the assigned team member's name in the Case Snapshot.
  const [teamMembers, setTeamMembers] = useState([]);
  // Application Details (sections 1-6) collapses by default so the attorney's
  // primary review pane (snapshot + docs) stays above the fold — but only when
  // there is nothing to chase. Below 100% funding readiness it starts open,
  // because every field the readiness gauge checks is edited in this panel:
  // leaving it shut states the problem while hiding where it gets fixed.
  //
  // A lazy initialiser, not an effect. An effect would re-run whenever
  // readiness changed and fight the attorney — collapsing the panel would
  // spring it back open, and filling in a missing field would slam it shut
  // mid-edit. The opening position is decided once, here.
  //
  // Computable at mount because `pct` ignores documents (see
  // computeFundingReadiness), so it does not wait on the async doc fetch.
  const [appDetailsExpanded, setAppDetailsExpanded] = useState(
    () => computeFundingReadiness(application, []).pct < 100
  );
  const fetchDocs = async () => {
    try {
      const data = await apiGet(`/applications/${application.application_id}/documents`);
      setDocs(Array.isArray(data) ? data : (data.documents || []));
    } catch (e) { /* swallow — readiness will just show "missing" until reload */ }
  };
  const fetchOffers = async () => {
    try {
      const data = await apiGet(`/applications/${application.application_id}/offers`);
      setOffers(Array.isArray(data) ? data : (data.offers || []));
    } catch (e) { /* timeline degrades gracefully — application events still render */ }
  };
  const fetchDocumentRequests = async () => {
    try {
      const data = await apiGet(`/applications/${application.application_id}/document-requests`);
      setDocumentRequests(data.requests || []);
    } catch (e) { /* timeline degrades gracefully — doc request events skipped */ }
  };
  const fetchTeamMembers = async () => {
    try {
      const data = await apiGet('/team-members');
      setTeamMembers(data.team_members || []);
    } catch (e) { /* snapshot will show "—" if list is unavailable */ }
  };
  useEffect(() => { fetchDocs(); fetchOffers(); fetchDocumentRequests(); fetchTeamMembers(); }, [application.application_id]);

  const getAuthHeaders = () => { const token = localStorage.getItem('access_token'); return token ? { 'Authorization': `Bearer ${token}` } : {}; };

  const handleValidateApplication = async () => {
    setValidating(true);
    try {
      const result = await apiPost(`/applications/${application.application_id}/validate`);
      toast.success('Application validated!'); setFormData({ ...formData, validated_at: result.validated_at, status: 'approved' }); setShowValidateModal(false); onUpdate();
    } catch (error) { toast.error(error.message || 'Failed to validate'); }
    finally { setValidating(false); }
  };

  const handleDeclineApplication = async () => {
    if (!declineFeedback.trim()) { toast.error('Please provide feedback'); return; }
    setDeclining(true);
    try {
      await apiPost(`/applications/${application.application_id}/decline`, { feedback: declineFeedback });
      toast.success('Application declined'); setShowDeclineModal(false); setDeclineFeedback(''); onUpdate();
    } catch (error) { toast.error(error.message || 'Failed to decline'); }
    finally { setDeclining(false); }
  };

  const handleDiscard = () => { setFormData({ ...originalData }); setErrors({}); };

  // Human labels for the save bar. Naming what will be saved beats "unsaved
  // changes", because the attorney has usually just touched several sections.
  const FIELD_LABELS = {
    firstName: 'First Name', lastName: 'Last Name', address1: 'Address', city: 'City',
    state: 'State', zip: 'Zip', phoneNumber: 'Phone', dateOfBirth: 'Date of Birth',
    caseType: 'Case Type', caseTypeOtherText: 'Case Type detail', incidentDate: 'Date of Incident',
    requestedAmount: 'Requested Amount', accidentState: 'Accident State',
    hasPriorFunding: 'Prior Funding', hasOfferOrSettled: 'Offers or Settled',
    insuranceCarrier: 'Insurance Carrier', insuranceCarrierOther: 'Carrier name',
    liabilityLimits: 'Liability Policy Limits', hasUmCoverage: 'UM Coverage', umLimits: 'UM Limits',
    attorneyEmail: 'Attorney Email', lawFirmName: 'Law Firm', lawFirmPhone: 'Law Firm Phone',
    lawyerName: 'Lawyer Name', assigned_team_member_id: 'Assigned Team Member',
    injuriesDescription: 'Injuries Description', hasStartedTreatment: 'Started Treatment',
    hadPtOrChiro: 'Physical Therapy / Chiropractor', hadSeriousInjections: 'Serious Injections',
    brokeBones: 'Broken Bones', hadSurgery: 'Surgery', otherInjuriesTreatments: 'Other', comments: 'Comments',
    childSupportLiens: 'Child Support Liens', inBankruptcy: 'Currently in Bankruptcy',
    internalNotes: 'Internal Notes', liabilityStatus: 'Liability Status', stageOfCase: 'Stage of Case',
    estimatedCaseValueMin: 'Estimated Case Value', estimatedCaseValueMax: 'Estimated Case Value',
    lienPosition: 'Lien Position', injuryTier: 'Injury Tier',
  };

  // What the attorney has actually changed, which drives the save bar and the
  // validation below. Compared loosely: a select writing '' over undefined is
  // not an edit, and treating it as one would raise the bar on page load.
  const changedFields = Object.keys(FIELD_LABELS).filter((f) => {
    const before = originalData[f]; const after = formData[f];
    if (before === undefined && (after === '' || after === undefined || after === null)) return false;
    return String(before ?? '') !== String(after ?? '');
  });
  const isDirty = changedFields.length > 0;
  const changedLabels = [...new Set(changedFields.map((f) => FIELD_LABELS[f]))];

  // Blanks never block. Applications arrive from `/apply` deliberately
  // incomplete, and the attorney is often the one filling the gaps — refusing
  // the save until a section is whole discarded the edits they just made.
  // Completeness is still enforced at the Approve button, which stays
  // disabled while APPROVAL_REQUIRED_FIELDS are missing.
  //
  // Only CHANGED fields are validated. validateSection used to scope this by
  // section for one reason, stated in its old comment: a malformed value in a
  // section the attorney isn't looking at must not block the one they are.
  // With a single save, "what changed" is the honest version of that scope —
  // a stale bad zip from `/apply` cannot trap them in an unrelated edit.
  const validateChanged = () => {
    const all = validateApplicationFormats(formData);
    const newErrors = {};
    changedFields.forEach((f) => { if (all[f]) newErrors[f] = all[f]; });
    setErrors(newErrors); return Object.keys(newErrors).length === 0;
  };

  // Numbers the notes save used to coerce on its own. Folding that button into
  // this one must not start storing "12,000" as a string.
  const buildPayload = () => ({
    ...formData,
    estimatedCaseValueMin: formData.estimatedCaseValueMin ? parseFloat(formData.estimatedCaseValueMin) : null,
    estimatedCaseValueMax: formData.estimatedCaseValueMax ? parseFloat(formData.estimatedCaseValueMax) : null,
  });

  const handleSaveClick = async () => {
    if (savingRef.current) return;
    if (!validateChanged()) { toast.error('Please fix the highlighted fields'); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      await apiPut(`/applications/${application.application_id}`, buildPayload());
      toast.success('Changes saved'); setOriginalData({ ...formData }); setErrors({});
    } catch (error) { toast.error(error.message || 'Failed to save'); }
    finally { savingRef.current = false; setSaving(false); }
  };

  const updateField = (field, value) => { setFormData({ ...formData, [field]: value }); if (errors[field]) setErrors({ ...errors, [field]: null }); };
  // Every section is editable at once now, so this is no longer a question
  // about which section is open — only about whether this attorney may edit.
  const isEditing = () => canEditApp;
  const getRadioDisplayValue = (value, options) => { const option = options.find(o => o.value === value); return option ? option.label : value || 'Not provided'; };

  // Paralegals share the lawyer dashboard but cannot approve/decline applications.
  const isParalegalView = currentUser?.role === 'paralegal';
  const canValidate = !isParalegalView && formData.status === 'pending_lawyer_review';
  const isValidated = !!formData.validated_at || formData.status === 'approved';
  const isDeclined = formData.status === 'declined';
  const isDraft = formData.status === 'draft';
  const canEditApp = !['approved', 'funded'].includes(formData.status);

  const renderDetailField = (label, editContent, displayValue, required) => (
    <div className="mcl-form-group">
      <label className="mcl-form-label">{label}{required && ' *'}</label>
      {editContent || <ReadOnlyValue value={displayValue} />}
    </div>
  );

  // Phase 1 derived values (used by snapshot + sticky bar + readiness)
  const clientName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || 'Unnamed Client';
  const requestedAmt = formData.requestedAmount ? `$${parseFloat(formData.requestedAmount).toLocaleString()}` : '-';
  const readiness = computeFundingReadiness(formData, docs);
  const missingDocs = computeMissingDocs(docs);
  const requiredActionLabel = canValidate
    ? 'Review and validate (or decline) this application'
    : isDraft ? 'Waiting for client to submit'
    : isValidated ? 'Validated — awaiting funder offers'
    : isDeclined ? 'Declined'
    : '';

  return (
    <div data-testid="lawyer-application-detail-view">
      {(() => {
        const currentIdx = siblingIds.indexOf(application.application_id);
        const hasList = currentIdx >= 0 && siblingIds.length > 1;
        const prevId = hasList && currentIdx > 0 ? siblingIds[currentIdx - 1] : null;
        const nextId = hasList && currentIdx < siblingIds.length - 1 ? siblingIds[currentIdx + 1] : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.85rem', flexWrap: 'wrap' }} data-testid="detail-nav-row">
            <BackLink onClick={onBack} label="Back to Applications" />
            {hasList && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="application-pager">
                <button
                  type="button"
                  className="mcl-btn mcl-btn-sm mcl-btn-outline"
                  onClick={() => prevId && onNavigate && onNavigate(prevId)}
                  disabled={!prevId}
                  data-testid="prev-application-btn"
                  aria-label="Previous application"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', opacity: prevId ? 1 : 0.45, cursor: prevId ? 'pointer' : 'not-allowed' }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)', whiteSpace: 'nowrap' }} data-testid="application-pager-position">
                  Application <strong style={{ color: 'var(--mcl-text-heading)' }}>{currentIdx + 1}</strong> of <strong style={{ color: 'var(--mcl-text-heading)' }}>{siblingIds.length}</strong>
                </span>
                <button
                  type="button"
                  className="mcl-btn mcl-btn-sm mcl-btn-outline"
                  onClick={() => nextId && onNavigate && onNavigate(nextId)}
                  disabled={!nextId}
                  data-testid="next-application-btn"
                  aria-label="Next application"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', opacity: nextId ? 1 : 0.45, cursor: nextId ? 'pointer' : 'not-allowed' }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* "Why Am I Reviewing This?" banner — contextual, pulled from application
          routing metadata so the attorney immediately understands how the case
          landed in their queue. Only shown while review is required. */}
      {canValidate && (() => {
        const clientDisplay = clientName;
        const firmDisplay = formData.lawFirmName || 'your firm';
        const submittedLabel = formatDate(formData.submitted_at) || null;
        // Preferred routing signal — attorneyEmail was captured on the client's application.
        const selectedByEmail = (formData.attorneyEmail || '').toLowerCase().trim();
        const currentEmail = (currentUser?.email || '').toLowerCase().trim();
        let reasonHeadline;
        if (selectedByEmail && currentEmail && selectedByEmail === currentEmail) {
          reasonHeadline = (
            <>
              <strong>{clientDisplay}</strong> selected <strong>you</strong> at <strong>{firmDisplay}</strong>
              {submittedLabel ? <> when submitting on <strong>{submittedLabel}</strong>.</> : ' when submitting this application.'}
            </>
          );
        } else if (formData.lawFirmName) {
          reasonHeadline = (
            <>
              <strong>{clientDisplay}</strong> selected <strong>{firmDisplay}</strong>
              {submittedLabel ? <> when submitting on <strong>{submittedLabel}</strong>.</> : ' when submitting this application.'}
            </>
          );
        } else {
          reasonHeadline = (
            <>
              This application was routed to your queue for attorney review
              {submittedLabel ? <> on <strong>{submittedLabel}</strong>.</> : '.'}
            </>
          );
        }
        return (
          <div
            data-testid="why-reviewing-banner"
            style={{
              background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '10px',
              padding: '0.85rem 1.1rem', marginBottom: '1rem', display: 'flex',
              alignItems: 'flex-start', gap: '0.7rem',
            }}
          >
            <Info size={18} style={{ color: '#1d4ed8', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ fontSize: '0.875rem', color: 'var(--mcl-text-body)', lineHeight: 1.55 }}>
              <div data-testid="why-reviewing-reason">{reasonHeadline}</div>
              <div style={{ marginTop: '0.35rem', fontSize: '0.82rem', color: 'var(--mcl-text-muted)' }}>
                Your review confirms: <span style={{ color: '#16a34a' }}>✓</span> active representation &nbsp;
                <span style={{ color: '#16a34a' }}>✓</span> case validity &nbsp;
                <span style={{ color: '#16a34a' }}>✓</span> authorization to proceed with funding review
              </div>
            </div>
          </div>
        );
      })()}

      {/* STICKY ACTION PANEL (#1) — always visible while scrolling */}
      <div
        data-testid="sticky-action-panel"
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: '#fff',
          border: '1px solid var(--mcl-border)',
          borderRadius: '10px',
          padding: '0.85rem 1.1rem',
          marginBottom: '1.25rem',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <StatusBadge status={formData.status} />
            <h2 className="mcl-page-title" style={{ margin: 0, fontSize: '1.05rem' }}>{clientName}</h2>
            <span style={{ color: 'var(--mcl-text-light)' }}>·</span>
            <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }} data-testid="sticky-requested-amount">{requestedAmt} requested</span>
          </div>
          {requiredActionLabel && (
            <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)', marginTop: '0.2rem' }}>
              {requiredActionLabel}
            </div>
          )}
        </div>
        {canValidate ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                className="mcl-btn mcl-btn-success"
                onClick={() => {
                  const missing = getMissingApprovalFields(formData);
                  if (missing.length > 0) {
                    toast.error(`Cannot approve — missing required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Expand Application Details to fill them in.`);
                    return;
                  }
                  setShowValidateModal(true);
                }}
                disabled={getMissingApprovalFields(formData).length > 0}
                title={getMissingApprovalFields(formData).length > 0 ? `Missing: ${getMissingApprovalFields(formData).join(', ')}` : 'Approve application'}
                data-testid="validate-application-btn"
                style={getMissingApprovalFields(formData).length > 0 ? { opacity: 0.55, cursor: 'not-allowed' } : {}}
              >
                <CheckCircle size={16} /> Approve
              </button>
              <button className="mcl-btn mcl-btn-outline" onClick={() => setShowDeclineModal(true)} data-testid="decline-application-btn" style={{ color: 'var(--mcl-danger)', borderColor: 'var(--mcl-danger)' }}>
                <XCircle size={16} /> Decline
              </button>
            </div>
            {getMissingApprovalFields(formData).length > 0 && (
              <span data-testid="approval-missing-fields-hint" style={{ fontSize: '0.7rem', color: '#dc2626', textAlign: 'right', maxWidth: '280px', lineHeight: 1.35 }}>
                Missing: {getMissingApprovalFields(formData).join(', ')}
              </span>
            )}
          </div>
        ) : (
          <span style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>App ID: {application.application_id}</span>
        )}
      </div>

      {/* Validation / Decline Banners */}
      {isValidated && (
        <div className="mcl-alert mcl-alert-success" style={{ marginBottom: '1.25rem' }}>
          <CheckCircle size={18} />
          <div><strong>Application Validated</strong><br /><span style={{ fontSize: '0.8rem' }}>Validated on {formatDate(formData.validated_at)}{formData.validated_by_name && ` by ${formData.validated_by_name}`}</span></div>
        </div>
      )}
      {isDeclined && (
        <div className="mcl-alert mcl-alert-danger" style={{ marginBottom: '1.25rem' }}>
          <XCircle size={18} />
          <div><strong>Application Declined</strong>{formData.client_feedback && <><br /><span style={{ fontSize: '0.8rem' }}>Feedback: {formData.client_feedback}</span></>}</div>
        </div>
      )}

      {/* CASE SNAPSHOT (#2) + READINESS + MISSING-INFO ALERTS — stacks on mobile */}
      <div className="mcl-snapshot-readiness-row" data-testid="case-snapshot-grid">
        {/* Snapshot card */}
        <div className="mcl-card" data-testid="case-snapshot-card" style={{ padding: '1rem 1.1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--mcl-text-muted)', marginBottom: '0.6rem' }}>Case Snapshot</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: '1rem', rowGap: '0.55rem', fontSize: '0.86rem' }}>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Client:</span> <strong>{clientName}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Requested:</span> <strong>{requestedAmt}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Case Type:</span> <strong>{getCaseTypeLabel(formData.caseType) || '—'}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Date of Incident:</span> <strong>{formatDateOnly(formData.incidentDate) || '—'}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Law Firm:</span> <strong>{formData.lawFirmName || '—'}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Attorney:</span> <strong>{formData.lawyerName || '—'}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Team Member:</span> <strong>{(() => {
              const tm = teamMembers.find(t => t.user_id === formData.assigned_team_member_id);
              return tm ? `${tm.first_name} ${tm.last_name}` : (formData.assigned_team_member_id ? 'Assigned' : '—');
            })()}</strong></div>
            <div><span style={{ color: 'var(--mcl-text-muted)' }}>Status:</span> <strong style={{ textTransform: 'capitalize' }}>{(formData.status || '').replace(/_/g, ' ')}</strong></div>
          </div>
        </div>

        {/* Readiness + missing-info alerts (combines #3 and #8) */}
        <div className="mcl-card" data-testid="funding-readiness-card" style={{ padding: '1rem 1.1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--mcl-text-muted)', marginBottom: '0.4rem' }}>Funding Readiness</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.45rem' }}>
            <span style={{ fontSize: '1.65rem', fontWeight: 800, color: readiness.pct >= 80 ? '#16a34a' : readiness.pct >= 50 ? '#d97706' : '#dc2626' }} data-testid="readiness-pct">{readiness.pct}%</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>{readiness.checks.filter(c => c.passed).length} of {readiness.checks.length} complete</span>
          </div>
          {/* Progress bar */}
          <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '999px', marginBottom: '0.65rem', overflow: 'hidden' }}>
            <div style={{ width: `${readiness.pct}%`, height: '100%', background: readiness.pct >= 80 ? '#16a34a' : readiness.pct >= 50 ? '#d97706' : '#dc2626', transition: 'width 200ms ease' }} />
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 0.7rem' }}>
            {readiness.checks.map(c => (
              <li key={c.key} style={{ fontSize: '0.78rem', color: c.passed ? 'var(--mcl-text-body)' : 'var(--mcl-text-muted)' }}>
                {c.passed
                  ? <CheckCircle size={12} style={{ color: '#16a34a', marginRight: '4px', verticalAlign: '-2px' }} />
                  : <AlertTriangle size={12} style={{ color: '#d97706', marginRight: '4px', verticalAlign: '-2px' }} />}
                {c.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* MISSING DOCUMENTS ALERTS (#3) */}
      {(missingDocs.required.length > 0 || missingDocs.recommended.length > 0) && (
        <div style={{ marginBottom: '1rem' }} data-testid="missing-info-alerts">
          {missingDocs.required.length > 0 && (
            <div className="mcl-alert mcl-alert-danger" style={{ marginBottom: '0.5rem' }} data-testid="missing-required-docs">
              <AlertTriangle size={18} />
              <div>
                <strong>Required before approval:</strong>{' '}
                {missingDocs.required.map(s => DOC_LABELS[s] || s).join(', ')}
              </div>
            </div>
          )}
          {missingDocs.recommended.length > 0 && (
            <div className="mcl-alert mcl-alert-warning" data-testid="missing-recommended-docs">
              <AlertTriangle size={18} />
              <div>
                <strong>Recommended (improves funder offers):</strong>{' '}
                {missingDocs.recommended.map(s => DOC_LABELS[s] || s).join(', ')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Funding Offers - visible for approved/funded applications */}
      {['approved', 'funded'].includes(formData.status) && (
        <CompareOffers applicationId={application.application_id} />
      )}

      {/* Internal Lawyer Notes (80%) + Activity Timeline (20%) — stacks on mobile */}
      <div className="mcl-notes-activity-row" data-testid="notes-timeline-row">
      <div className="mcl-card" style={{ borderColor: '#fde68a' }}>
        <div className="mcl-card-header" style={{ borderBottomColor: '#fde68a' }}>
          <h3 className="mcl-card-header-title" style={{ color: '#92400e' }}>Internal-Lawyer Notes</h3>
          <span style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 500 }}>Not visible to client</span>
        </div>
        <div className="mcl-card-body">
          <div className="mcl-form-row">
            <div className="mcl-form-group">
              <label className="mcl-form-label" style={{ color: '#92400e' }}>Liability Status</label>
              <Select value={formData.liabilityStatus || ''} onValueChange={(value) => updateField('liabilityStatus', value)}>
                <SelectTrigger data-testid="select-liability-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent><SelectItem value="cleared">Cleared</SelectItem><SelectItem value="disputed">Disputed</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label" style={{ color: '#92400e' }}>Stage of Case</label>
              <Select value={formData.stageOfCase || ''} onValueChange={(value) => updateField('stageOfCase', value)}>
                <SelectTrigger data-testid="select-stage-of-case"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent><SelectItem value="pre_suit">Pre-suit</SelectItem><SelectItem value="in_litigation">In Litigation</SelectItem><SelectItem value="trial_scheduled">Trial Scheduled</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mcl-form-row">
            <div className="mcl-form-group">
              <label className="mcl-form-label" style={{ color: '#92400e' }}>Estimated Case Value (Range)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input className="mcl-form-input" type="text" value={formatCurrency(formData.estimatedCaseValueMin || '')} onChange={(e) => updateField('estimatedCaseValueMin', parseCurrency(e.target.value))} placeholder="Min ($)" data-testid="input-case-value-min" />
                <span style={{ color: '#92400e' }}>&ndash;</span>
                <input className="mcl-form-input" type="text" value={formatCurrency(formData.estimatedCaseValueMax || '')} onChange={(e) => updateField('estimatedCaseValueMax', parseCurrency(e.target.value))} placeholder="Max ($)" data-testid="input-case-value-max" />
              </div>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label" style={{ color: '#92400e' }}>Injury Tier</label>
              <Select value={formData.injuryTier || ''} onValueChange={(value) => updateField('injuryTier', value)}>
                <SelectTrigger data-testid="select-injury-tier"><SelectValue placeholder="Select tier" /></SelectTrigger>
                <SelectContent><SelectItem value="minor">Minor</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="severe">Severe</SelectItem><SelectItem value="catastrophic">Catastrophic</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mcl-form-group" style={{ maxWidth: '50%' }}>
            <label className="mcl-form-label" style={{ color: '#92400e' }}>Lien Position</label>
            <Select value={formData.lienPosition || ''} onValueChange={(value) => updateField('lienPosition', value)}>
              <SelectTrigger data-testid="select-lien-position"><SelectValue placeholder="Select position" /></SelectTrigger>
              <SelectContent><SelectItem value="1st">1st</SelectItem><SelectItem value="2nd">2nd</SelectItem><SelectItem value="unknown">Unknown</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label" style={{ color: '#92400e' }}>Additional Notes</label>
            <textarea className="mcl-form-textarea" value={formData.internalNotes || ''} onChange={(e) => updateField('internalNotes', e.target.value)} rows={4} placeholder="Case strength assessment, settlement potential, etc." data-testid="internal-notes" />
          </div>
          {/* No Save button here. handleSaveClick has always PUT internalNotes
              with the rest of formData, so a second button onto the same
              endpoint was two ways to do one thing. The floating bar saves it. */}
        </div>
      </div>

      {/* Activity Timeline — newest first, scrollable */}
      <ActivityTimelineCard application={formData} offers={offers} documentRequests={documentRequests} />
      </div>{/* end notes-activity-row */}

      {/* Documents — moved above the application form so attorneys can review uploads first */}
      {/* Requested Documents (funder asks) — outstanding + fulfilled list with per-row upload */}
      <DocumentRequestsCard applicationId={application.application_id} mode="fulfill" getAuthHeaders={getAuthHeaders} onChanged={fetchDocumentRequests} />
      <SectionCard title="Messages" className="mb-3">
        <MessagesPanel applicationId={application.application_id} currentUser={currentUser} />
      </SectionCard>

      {/* Documents are accepted at any application status — supporting records
          can be added after a case is approved or funded. */}
      <DocumentUpload applicationId={application.application_id} canUpload={true} getAuthHeaders={getAuthHeaders} userRole="lawyer" />

      {/* Application Details (Sections 1-6) — collapsible to keep the review focused */}
      <div className="mcl-card" data-testid="application-details-card" style={{ marginTop: '1.25rem', marginBottom: '1.25rem', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setAppDetailsExpanded(v => !v)}
          aria-expanded={appDetailsExpanded}
          data-testid="toggle-application-details"
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', padding: '0.95rem 1.1rem', cursor: 'pointer', textAlign: 'left' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontWeight: 700, color: 'var(--mcl-text-heading)', fontSize: '0.95rem' }}>
            {appDetailsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Application Details
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>
            {appDetailsExpanded ? 'Hide' : 'Show'} client-submitted form (Sections 1-6)
          </span>
        </button>
        {appDetailsExpanded && (
        <div style={{ padding: '1rem 1.1rem 1.1rem 1.1rem', borderTop: '1px solid var(--mcl-border)' }} data-testid="application-details-body">

      {/* Section 1: My Information */}
      <EditableFormSection title="1. Plaintiff Information" sectionKey="myInformation" isEditing isSaving={saving} canEdit={false}>
        <div className="mcl-form-row">
          {renderDetailField("First Name", isEditing('myInformation') && <input className={`mcl-form-input ${errors.firstName ? 'is-invalid' : ''}`} value={formData.firstName || ''} onChange={(e) => updateField('firstName', e.target.value)} data-testid="edit-first-name" />, formData.firstName, true)}
          {renderDetailField("Last Name", isEditing('myInformation') && <input className={`mcl-form-input ${errors.lastName ? 'is-invalid' : ''}`} value={formData.lastName || ''} onChange={(e) => updateField('lastName', e.target.value)} data-testid="edit-last-name" />, formData.lastName, true)}
        </div>
        {renderDetailField("Address", isEditing('myInformation') && <input className="mcl-form-input" value={formData.address1 || ''} onChange={(e) => updateField('address1', e.target.value)} />, [formData.address1, formData.address2].filter(Boolean).join(', '), false)}
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {renderDetailField("City", isEditing('myInformation') && <input className="mcl-form-input" value={formData.city || ''} onChange={(e) => updateField('city', e.target.value)} />, formData.city, false)}
          {renderDetailField("State", isEditing('myInformation') && <Select value={formData.state || ''} onValueChange={(v) => updateField('state', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>, US_STATES.find(s => s.value === formData.state)?.label || formData.state, false)}
          {renderDetailField("Zip", isEditing('myInformation') && <input className="mcl-form-input" inputMode="numeric" value={formData.zip || ''} onChange={(e) => updateField('zip', formatZipCode(e.target.value))} data-testid="edit-zip" />, formData.zip, false)}
        </div>
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {renderDetailField("Phone", isEditing('myInformation') && <input className="mcl-form-input" value={formatPhoneNumber(formData.phoneNumber || '')} onChange={(e) => updateField('phoneNumber', parsePhoneNumber(e.target.value))} />, formatPhoneNumber(formData.phoneNumber), false)}
          {renderDetailField("Date of Birth", isEditing('myInformation') && <input className="mcl-form-input" type="date" value={formData.dateOfBirth || ''} onChange={(e) => updateField('dateOfBirth', e.target.value)} />, formatDateOnly(formData.dateOfBirth), false)}
          {renderDetailField("SSN (Last 4)", null, formData.ssn ? `****${formData.ssn}` : null, false)}
        </div>
      </EditableFormSection>

      {/* Sections 2 & 3 side by side */}
      <div className="mcl-sections-row">
      {/* Section 2: Case Details */}
      <EditableFormSection title="2. Case Details" sectionKey="caseDetails" isEditing isSaving={saving} canEdit={false}>
        {renderDetailField("Case Type", isEditing('caseDetails') && <Select value={formData.caseType || ''} onValueChange={(v) => updateField('caseType', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{CASE_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>, getCaseTypeLabel(formData.caseType), true)}
        <div className="mcl-form-row">
        {renderDetailField("Date of Incident", isEditing('caseDetails') && <input className="mcl-form-input" type="date" value={formData.incidentDate || ''} onChange={(e) => updateField('incidentDate', e.target.value)} />, formatDateOnly(formData.incidentDate), false)}
        {renderDetailField("Requested Amount", isEditing('caseDetails') && <input className="mcl-form-input" value={formatCurrency(formData.requestedAmount)} onChange={(e) => updateField('requestedAmount', parseCurrency(e.target.value))} />, formData.requestedAmount ? `$${parseFloat(formData.requestedAmount).toLocaleString()}` : null, true)}
        </div>
        {renderDetailField("Accident State", isEditing('caseDetails') && <Select value={formData.accidentState || ''} onValueChange={(v) => updateField('accidentState', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>, US_STATES.find(s => s.value === formData.accidentState)?.label || formData.accidentState, true)}
        <div className="mcl-form-row">
        {renderDetailField("Prior Funding", isEditing('caseDetails') && <RadioGroup name="hasPriorFunding" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasPriorFunding} onChange={(v) => updateField('hasPriorFunding', v)} />, getRadioDisplayValue(formData.hasPriorFunding, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), false)}
        {renderDetailField("Offers or Settled", isEditing('caseDetails') && <RadioGroup name="hasOfferOrSettled" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasOfferOrSettled} onChange={(v) => updateField('hasOfferOrSettled', v)} />, getRadioDisplayValue(formData.hasOfferOrSettled, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), false)}
        </div>
      </EditableFormSection>

      {/* Section 3: Insurance Information */}
      <EditableFormSection title="3. Insurance Information" sectionKey="insuranceInfo" isEditing isSaving={saving} canEdit={false}>
        {renderDetailField("Insurance Carrier", isEditing('insuranceInfo') && <Select value={formData.insuranceCarrier || ''} onValueChange={(v) => updateField('insuranceCarrier', v)}><SelectTrigger><SelectValue placeholder="Select carrier" /></SelectTrigger><SelectContent>{INSURANCE_CARRIERS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent></Select>, getInsuranceCarrierLabel(formData.insuranceCarrier), true)}
        {(formData.insuranceCarrier === 'other') && renderDetailField("Carrier Name", isEditing('insuranceInfo') && <input className="mcl-form-input" value={formData.insuranceCarrierOther || ''} onChange={(e) => updateField('insuranceCarrierOther', e.target.value)} />, formData.insuranceCarrierOther, true)}
        {renderDetailField("Liability Policy Limits", isEditing('insuranceInfo') && <Select value={formData.liabilityLimits || ''} onValueChange={(v) => updateField('liabilityLimits', v)}><SelectTrigger><SelectValue placeholder="Select limits" /></SelectTrigger><SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent></Select>, getLiabilityLimitsLabel(formData.liabilityLimits), false)}
        {renderDetailField("UM Coverage", isEditing('insuranceInfo') && <RadioGroup name="hasUmCoverage" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasUmCoverage} onChange={(v) => updateField('hasUmCoverage', v)} />, getRadioDisplayValue(formData.hasUmCoverage, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), true)}
        {formData.hasUmCoverage === 'yes' && (
          <>
            {renderDetailField("UM Policy Limits", isEditing('insuranceInfo') && <Select value={formData.umLimits || ''} onValueChange={(v) => updateField('umLimits', v)}><SelectTrigger><SelectValue placeholder="Select UM limits" /></SelectTrigger><SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent></Select>, getLiabilityLimitsLabel(formData.umLimits), true)}
            <div className="mcl-form-row">
              {renderDetailField("UM Policy Number", isEditing('insuranceInfo') && <input className="mcl-form-input" value={formData.umPolicyNumber || ''} onChange={(e) => updateField('umPolicyNumber', e.target.value)} />, formData.umPolicyNumber, false)}
              {renderDetailField("UM Claim Number", isEditing('insuranceInfo') && <input className="mcl-form-input" value={formData.umClaimNumber || ''} onChange={(e) => updateField('umClaimNumber', e.target.value)} />, formData.umClaimNumber, false)}
            </div>
          </>
        )}
      </EditableFormSection>
      </div>{/* end mcl-sections-row */}

      {/* Section 4: Law Firm */}
      <EditableFormSection title="4. Law Firm Information" sectionKey="lawFirmInfo" isEditing isSaving={saving} canEdit={false}>
        <div className="mcl-form-row">
          {renderDetailField("Lawyer Name", isEditing('lawFirmInfo') && <input className="mcl-form-input" value={formData.lawyerName || ''} onChange={(e) => updateField('lawyerName', e.target.value)} />, formData.lawyerName, false)}
          {renderDetailField("Attorney Email", isEditing('lawFirmInfo') && <input className="mcl-form-input" value={formData.attorneyEmail || ''} onChange={(e) => updateField('attorneyEmail', e.target.value)} autoComplete="off" />, formData.attorneyEmail, true)}
        </div>
        <div className="mcl-form-row">
          {renderDetailField("Law Firm", isEditing('lawFirmInfo') && <input className="mcl-form-input" value={formData.lawFirmName || ''} onChange={(e) => updateField('lawFirmName', e.target.value)} />, formData.lawFirmName, true)}
          {renderDetailField("Law Firm Phone", isEditing('lawFirmInfo') && <input className="mcl-form-input" value={formatPhoneNumber(formData.lawFirmPhone || '')} onChange={(e) => updateField('lawFirmPhone', parsePhoneNumber(e.target.value))} />, formatPhoneNumber(formData.lawFirmPhone), true)}
        </div>
      </EditableFormSection>

      {/* Assigned Team Member — replaces legacy Support Staff free-text. Lawyers only;
          paralegals see read-only display in the assigned card. */}
      <TeamMemberSelect
        applicationId={application.application_id}
        currentAssignedId={formData.assigned_team_member_id || ''}
        canAssign={canEditApp}
        onAssigned={(id) => { setFormData(fd => ({ ...fd, assigned_team_member_id: id })); setOriginalData(od => ({ ...od, assigned_team_member_id: id })); }}
      />

      {/* Section 5: Injuries */}
      <EditableFormSection title="5. Injuries & Treatments" sectionKey="treatment" isEditing isSaving={saving} canEdit={false}>
        {renderDetailField("Injuries Description", isEditing('treatment') && <textarea className="mcl-form-textarea" value={formData.injuriesDescription || ''} onChange={(e) => updateField('injuriesDescription', e.target.value)} rows={3} />, formData.injuriesDescription, false)}
        {renderDetailField("Started Treatment", isEditing('treatment') && <RadioGroup name="hasStartedTreatment" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasStartedTreatment} onChange={(v) => updateField('hasStartedTreatment', v)} />, getRadioDisplayValue(formData.hasStartedTreatment, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), false)}
        {formData.hasStartedTreatment === 'yes' && (
          <>
            {renderDetailField(
              "Where did you go?",
              isEditing('treatment') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {TREATMENT_LOCATIONS.map((option) => (
                    <label key={option.value} className="mcl-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.treatmentLocations?.includes(option.value) || false}
                        onChange={(e) => {
                          const current = formData.treatmentLocations || [];
                          updateField('treatmentLocations', e.target.checked ? [...current, option.value] : current.filter(v => v !== option.value));
                        }}
                        data-testid={`edit-checkbox-location-${option.value}`}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              ),
              formatTreatmentLocations(formData.treatmentLocations),
              false
            )}
            {renderDetailField(
              "Treatments Completed",
              isEditing('treatment') && <RadioGroup name="treatmentsCompleted" options={TREATMENTS_COMPLETED_OPTIONS} value={formData.treatmentsCompleted} onChange={(v) => updateField('treatmentsCompleted', v)} />,
              getRadioDisplayValue(formData.treatmentsCompleted, TREATMENTS_COMPLETED_OPTIONS),
              false
            )}
            {renderDetailField(
              "Surgical Recommendations",
              isEditing('treatment') && <RadioGroup name="surgicalRecommendations" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.surgicalRecommendations} onChange={(v) => updateField('surgicalRecommendations', v)} />,
              getRadioDisplayValue(formData.surgicalRecommendations, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]),
              false
            )}
          </>
        )}
        {renderDetailField("Physical Therapy / Chiropractor", isEditing('treatment') && <RadioGroup name="hadPtOrChiro" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }]} value={formData.hadPtOrChiro} onChange={(v) => updateField('hadPtOrChiro', v)} />, getRadioDisplayValue(formData.hadPtOrChiro, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }]), false)}
        {renderDetailField("Serious Injections", isEditing('treatment') && <RadioGroup name="hadSeriousInjections" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSeriousInjections} onChange={(v) => updateField('hadSeriousInjections', v)} />, getRadioDisplayValue(formData.hadSeriousInjections, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }, { value: 'expecting', label: 'Expecting' }]), false)}
        {renderDetailField("Broken Bones", isEditing('treatment') && <RadioGroup name="brokeBones" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.brokeBones} onChange={(v) => updateField('brokeBones', v)} />, getRadioDisplayValue(formData.brokeBones, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]), false)}
        {renderDetailField("Surgery", isEditing('treatment') && <RadioGroup name="hadSurgery" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSurgery} onChange={(v) => updateField('hadSurgery', v)} />, getRadioDisplayValue(formData.hadSurgery, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'expecting', label: 'Expecting' }]), false)}
        {renderDetailField("Other", isEditing('treatment') && <textarea className="mcl-form-textarea" value={formData.otherInjuriesTreatments || ''} onChange={(e) => updateField('otherInjuriesTreatments', e.target.value)} rows={3} />, formData.otherInjuriesTreatments, false)}
        {renderDetailField("Comments", isEditing('treatment') && <textarea className="mcl-form-textarea" value={formData.comments || ''} onChange={(e) => updateField('comments', e.target.value)} rows={3} />, formData.comments, false)}
      </EditableFormSection>

      {/* Section 6: Additional */}
      <EditableFormSection title="6. Additional Information" sectionKey="additionalInfo" isEditing isSaving={saving} canEdit={false}>
        {renderDetailField("Child Support Liens", isEditing('additionalInfo') && <RadioGroup name="childSupportLiens" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.childSupportLiens} onChange={(v) => updateField('childSupportLiens', v)} />, getRadioDisplayValue(formData.childSupportLiens, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]), false)}
        {renderDetailField("Currently in Bankruptcy", isEditing('additionalInfo') && <RadioGroup name="inBankruptcy" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.inBankruptcy} onChange={(v) => updateField('inBankruptcy', v)} />, getRadioDisplayValue(formData.inBankruptcy, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), false)}
      </EditableFormSection>

        </div>
        )}
      </div>
      {/* end Application Details collapsible */}

      {/* FLOATING SAVE — one save for the whole application, replacing the six
          per-section Edit/Save pairs. It appears only once something has
          changed and names the fields it will write, because by the time an
          attorney reaches for it they have usually touched several sections.
          Sticky rather than fixed so it sits inside the content column and
          cannot cover the sidebar. */}
      {canEditApp && isDirty && (
        <div
          data-testid="floating-save-bar"
          style={{
            position: 'sticky', bottom: '1rem', zIndex: 60,
            marginTop: '1.25rem', display: 'flex', justifyContent: 'center',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem', maxWidth: '100%',
            padding: '0.7rem 0.9rem 0.7rem 1.15rem',
            background: 'var(--mcl-text-heading)', borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.28)', flexWrap: 'wrap',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: '#fff', minWidth: 0 }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--mcl-warning)', flexShrink: 0 }} />
              <span data-testid="floating-save-summary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '46ch' }}>
                Unsaved changes &middot; {changedLabels.join(', ')}
              </span>
            </span>
            <div style={{ display: 'flex', gap: '0.45rem', flexShrink: 0 }}>
              <button
                type="button"
                className="mcl-btn mcl-btn-sm"
                onClick={handleDiscard}
                disabled={saving}
                data-testid="floating-discard-btn"
                style={{ background: 'transparent', color: '#cbd5e1', borderColor: '#334155' }}
              >
                Discard
              </button>
              <button
                type="button"
                className="mcl-btn mcl-btn-sm mcl-btn-success"
                onClick={handleSaveClick}
                disabled={saving}
                data-testid="floating-save-btn"
              >
                <Save size={14} /> {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validate Modal */}
      <ConfirmDialog
        open={showValidateModal}
        onOpenChange={setShowValidateModal}
        tone="success"
        title="Validate Application"
        description="This publishes the application to funders, who can then review it and make offers."
        warning="This cannot be undone."
        confirmLabel="Validate Application"
        busyLabel="Validating..."
        busy={validating}
        onConfirm={handleValidateApplication}
        testId="confirm-validate-btn"
      />

      {/* Decline Modal */}
      <ConfirmDialog
        open={showDeclineModal}
        onOpenChange={setShowDeclineModal}
        tone="danger"
        title="Decline Application"
        description="The client will see your feedback, so be specific about what needs to change."
        confirmLabel="Decline Application"
        busyLabel="Declining..."
        busy={declining}
        confirmDisabled={!declineFeedback.trim()}
        onConfirm={handleDeclineApplication}
        testId="confirm-decline-btn"
      >
        <textarea className="mcl-form-textarea" value={declineFeedback} onChange={(e) => setDeclineFeedback(e.target.value)} placeholder="Enter feedback for the client..." rows={4} data-testid="decline-feedback-input" />
      </ConfirmDialog>
    </div>
  );
};

/* ================================================================
   Profile Tab
   ================================================================ */

/* ================================================================
   Client Offers Tab — All offers across the lawyer's clients
   ================================================================ */
const ClientOffersTab = ({ onViewApplication }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        setOffers(await apiGet('/lawyer/client-offers'));
      } catch (e) { console.error('Failed to fetch client offers:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const fmt = (v) => v ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

  const filteredOffers = filter === 'all' ? offers : offers.filter(o => o.status === filter);

  // Group by application for summary stats
  const appGroups = {};
  offers.forEach(o => {
    if (!appGroups[o.application_id]) appGroups[o.application_id] = { client: o.client_name, offers: [] };
    appGroups[o.application_id].offers.push(o);
  });
  const totalPending = offers.filter(o => o.status === 'submitted').length;
  const totalAccepted = offers.filter(o => o.status === 'accepted').length;
  const totalDeclined = offers.filter(o => o.status === 'declined').length;
  const clientsWithOffers = Object.keys(appGroups).length;

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--mcl-text-muted)' }}>Loading offers...</div>;

  return (
    <div data-testid="client-offers-tab">
      <PageHeader title="Client Offers" subtitle="Review funding offers made to your clients' applications" />

      {/* Summary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div className="mcl-card" data-testid="kpi-clients-with-offers">
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Clients with Offers</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--mcl-text-heading)' }}>{clientsWithOffers}</div>
          </div>
          <div style={{ height: '3px', background: '#2563eb' }} />
        </div>
        <div className="mcl-card" data-testid="kpi-pending-offers">
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pending Review</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ea580c' }}>{totalPending}</div>
          </div>
          <div style={{ height: '3px', background: '#ea580c' }} />
        </div>
        <div className="mcl-card" data-testid="kpi-accepted-offers">
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accepted</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>{totalAccepted}</div>
          </div>
          <div style={{ height: '3px', background: '#059669' }} />
        </div>
        <div className="mcl-card" data-testid="kpi-total-offers">
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Offers</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--mcl-text-heading)' }}>{offers.length}</div>
          </div>
          <div style={{ height: '3px', background: '#6b7280' }} />
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: `All (${offers.length})` },
          { id: 'submitted', label: `Pending (${totalPending})` },
          { id: 'accepted', label: `Accepted (${totalAccepted})` },
          { id: 'declined', label: `Declined (${totalDeclined})` },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            style={{
              padding: '6px 14px', borderRadius: '8px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', border: '1px solid',
              background: filter === f.id ? 'var(--mcl-primary)' : '#fff',
              color: filter === f.id ? '#fff' : 'var(--mcl-text-heading)',
              borderColor: filter === f.id ? 'var(--mcl-primary)' : 'var(--mcl-border)',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Offers Table */}
      <SectionCard title={`Offers (${filteredOffers.length})`}>
        {filteredOffers.length === 0 ? (
          <EmptyState icon={DollarSign} title="No offers found" text={filter === 'all' ? 'No funders have submitted offers for your clients yet.' : `No ${filter} offers.`} />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table" data-testid="client-offers-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Funder</th>
                  <th style={{ textAlign: 'right' }}>Advance</th>
                  <th style={{ textAlign: 'right' }}>Total Obligation</th>
                  <th style={{ textAlign: 'center' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Max Cap</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.map((offer, idx) => {
                  const statusStyles = {
                    submitted: { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
                    accepted: { bg: '#dcfce7', color: '#166534', label: 'Accepted' },
                    declined: { bg: '#fee2e2', color: '#991b1b', label: 'Declined' },
                  };
                  const s = statusStyles[offer.status] || statusStyles.submitted;
                  return (
                    <tr key={offer.offer_id || idx} data-testid={`offer-row-${idx}`} style={offer.status === 'declined' ? { opacity: 0.6 } : {}}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.875rem' }}>{offer.client_name || 'Client'}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)' }}>{getCaseTypeLabel(offer.case_type)}</div>
                      </td>
                      <td style={{ fontWeight: 500, fontSize: '0.875rem' }}>{offer.funder_company || 'Funder'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#065f46' }}>${fmt(offer.advance_amount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>${fmt(offer.total_funded_amount)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{offer.annualized_rate}%</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>${fmt(offer.maximum_cap)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ padding: '2px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.8rem' }}>
                        {(offer.submitted_at ? formatDate(offer.submitted_at) : '-')}
                      </td>
                      <td>
                        <button className="mcl-action-btn primary" onClick={() => onViewApplication(offer.application_id)} data-testid={`view-app-${idx}`} style={{ fontSize: '0.75rem' }}>
                          <Eye size={13} /> View App
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};


/* ================================================================
   Refer a Client Tab
   ================================================================ */
const ReferClientTab = ({ user, onSuccess }) => {
  const [formData, setFormData] = useState({
    clientFirstName: '', clientLastName: '', clientEmail: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.clientFirstName.trim()) newErrors.clientFirstName = 'Required';
    if (!formData.clientLastName.trim()) newErrors.clientLastName = 'Required';
    if (!formData.clientEmail.trim()) newErrors.clientEmail = 'Required';
    else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) newErrors.clientEmail = 'Invalid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await apiPost('/lawyer/refer-client', formData);
      toast.success('Referral sent! Your client will receive an email.');
      setSubmitted(true);
    } catch (error) { toast.error(error.message || 'Failed to send referral'); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div data-testid="refer-client-tab">
        <PageHeader title="Refer a Client" subtitle="Invite your client to apply for pre-settlement funding" />
        <SectionCard>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--mcl-success-bg, #dcfce7)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <CheckCircle size={32} style={{ color: 'var(--mcl-success, #16a34a)' }} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--mcl-text-heading)', marginBottom: '0.75rem' }}>Referral Sent Successfully</h2>
            <p style={{ color: 'var(--mcl-text-body)', maxWidth: 480, margin: '0 auto 2rem', lineHeight: 1.6 }}>
              An email has been sent to <strong>{formData.clientEmail}</strong> with instructions to create an account and complete their application.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button className="mcl-btn mcl-btn-primary" onClick={() => { setSubmitted(false); setFormData({ clientFirstName: '', clientLastName: '', clientEmail: '' }); }} data-testid="refer-another-btn">
                <UserPlus size={16} /> Refer Another Client
              </button>
              <button className="mcl-btn mcl-btn-outline" onClick={onSuccess} data-testid="back-to-applications-btn">
                Back to Applications
              </button>
            </div>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div data-testid="refer-client-tab">
      <PageHeader title="Refer a Client" subtitle="Invite your client to apply for pre-settlement funding" />
      <form onSubmit={handleSubmit}>
        <SectionCard title="Client Information">
          <div className="mcl-form-row">
            <div className="mcl-form-group">
              <label className="mcl-form-label">First Name *</label>
              <input className={`mcl-form-input ${errors.clientFirstName ? 'is-invalid' : ''}`} value={formData.clientFirstName} onChange={(e) => updateField('clientFirstName', e.target.value)} data-testid="input-client-first-name" placeholder="Client's first name" />
              {errors.clientFirstName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.clientFirstName}</div>}
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Last Name *</label>
              <input className={`mcl-form-input ${errors.clientLastName ? 'is-invalid' : ''}`} value={formData.clientLastName} onChange={(e) => updateField('clientLastName', e.target.value)} data-testid="input-client-last-name" placeholder="Client's last name" />
              {errors.clientLastName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.clientLastName}</div>}
            </div>
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Email *</label>
            <input className={`mcl-form-input ${errors.clientEmail ? 'is-invalid' : ''}`} type="email" value={formData.clientEmail} onChange={(e) => updateField('clientEmail', e.target.value)} data-testid="input-client-email" placeholder="client@example.com" />
            {errors.clientEmail && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.clientEmail}</div>}
          </div>
        </SectionCard>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
          <button type="submit" className="mcl-btn mcl-btn-primary" disabled={submitting} data-testid="submit-referral-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {submitting ? <><Clock size={16} className="animate-spin" /> Sending...</> : <><UserPlus size={16} /> Send Referral</>}
          </button>
          <button type="button" className="mcl-btn mcl-btn-outline" onClick={onSuccess} data-testid="cancel-referral-btn">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default LawyerDashboard;
